import { BreakPointCondition, UI, useBreakpointIf, HStack } from '@genial/components';
import { Spacing } from '@genial/design-tokens';
import { RouteProp, useRoute } from '@react-navigation/native';
import { Box, FlatList } from 'native-base';
import React, { useEffect, useState } from 'react';
import { RecoilState, RecoilValueReadOnly, useSetRecoilState } from 'recoil';

import { TableHeadItemRecurse } from '../../../../../components/TableHead';
import { useRecoilQuery } from '../../../../../hooks/useRecoilQuery';
import { SignedInRootStackParamList } from '../../../../../routes/types';
import { useCustomerAssetsDetailsActions } from '../../../../../store/customerAssetDetail/actions';
import {
  AbstractAssetsQueryData,
  AssetsFilterProps,
} from '../../../../../store/customerAssetDetail/types';
import { IInvestedAssetsNormalized } from '../../../../../store/customerDetail/types';
import { DynamicAssetTableProps } from '../../DynamicAsset';

import { ProductTableMobile, ProductTableMobileProps } from './components/Mobile';
import { ProductTableTablet, ProductTableTabletProps } from './components/Tablet';
import { LoadingTable, NotFoundTable } from './constants';
import {
  renderDynamicLoadingFooter,
  renderDynamicTableHead,
  useDynamicTableMaxHeight,
} from './utils';

type ProductsQueryParams = {
  accountNumber: number;
};

type ProductsQueryResult<T> = RecoilValueReadOnly<
  { data: AbstractAssetsQueryData<T>; totalInvestedValue?: number } | undefined
>;

type ProductsFiltersQueryResult = RecoilValueReadOnly<{
  data: AssetsFilterProps[];
  totalInvestedValue?: number;
}>;

export interface IDynamicProductSearchItem<T> {
  item: T;
  product: DynamicAssetTableProps['product'];
  quoteButtonInfoGetter?: ProductTableTabletProps<T>['quoteButtonInfoGetter'];
  recommendedPortfolioInfoGetter?: ProductTableTabletProps<T>['recommendedPortfolioInfoGetter'];
}

export interface DynamicProductTableProps<T> {
  product: DynamicAssetTableProps['product'];
  productAtom: RecoilState<{ [key: string]: IInvestedAssetsNormalized | undefined }>;
  tableHeadItems: TableHeadItemRecurse[];
  mobileFields: ProductTableMobileProps<T>['fields'];
  tabletFields?: ProductTableTabletProps<T>['fields'];
  productsQuery: (param: ProductsQueryParams) => ProductsQueryResult<T>;
  filtersQuery: (param: ProductsQueryParams) => ProductsFiltersQueryResult;
  ProductItem: React.FC<IDynamicProductSearchItem<T>>;
  tabletColumns: number;
  tabletRows?: number;
  tabletSecondaryInfo?: React.FC<{ product: T }>;
  mobileColumns: number;
  mobileRows?: number;
  mobileSecondaryInfo?: React.FC<{ product: T }>;
  quoteButtonInfoGetter?: ProductTableTabletProps<T>['quoteButtonInfoGetter'];
  recommendedPortfolioInfoGetter?: ProductTableTabletProps<T>['recommendedPortfolioInfoGetter'];
  onData?: (data: T[]) => void;
}

const ItemSeparator = () => <Box height={Spacing['stack.nano']} />;

export const DynamicProductTable = <T,>({
  product,
  productAtom,
  productsQuery,
  filtersQuery,
  mobileFields,
  tableHeadItems,
  ProductItem,
  tabletColumns,
  tabletSecondaryInfo,
  tabletRows = 1,
  mobileColumns,
  mobileRows = 1,
  mobileSecondaryInfo,
  quoteButtonInfoGetter,
  recommendedPortfolioInfoGetter,
  tabletFields,
  onData,
}: DynamicProductTableProps<T>) => {
  const { params } = useRoute<RouteProp<SignedInRootStackParamList, 'CustomersAssetsDetails'>>();
  const columns = tableHeadItems.length;
  const setProductAtom = useSetRecoilState(productAtom);
  const isMobile = useBreakpointIf(BreakPointCondition.EqualOrDown, 'sm');
  const isTablet = useBreakpointIf(BreakPointCondition.EqualOrDown, 'lg');

  const [allData, setAllData] = useState<T[]>([]);
  const maxHeight = useDynamicTableMaxHeight();

  const {
    handleUpdateCustomerAssetsDetailsFilters,
    handleUpdateCustomerAssetsDetailsFiltersLoading,
  } = useCustomerAssetsDetailsActions(product.marketTypeNormalized);

  const { loading, data, error, refresher } = useRecoilQuery(
    productsQuery({ accountNumber: params.accountNumber })
  );

  const { loading: filtersLoading, data: filtersData } = useRecoilQuery(
    filtersQuery({ accountNumber: params.accountNumber })
  );

  useEffect(() => {
    if (onData) {
      onData(data?.data || []);
    }
  }, [data, onData]);

  useEffect(() => {
    if (error) {
      setAllData([]);
    }
  }, [error]);

  useEffect(() => {
    handleUpdateCustomerAssetsDetailsFiltersLoading(filtersLoading);
    if (filtersLoading || !filtersData?.data) {
      return;
    }
    handleUpdateCustomerAssetsDetailsFilters(filtersData.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersData, filtersLoading]);

  useEffect(() => {
    const items = data?.data;

    if (!loading) {
      if (data?.totalInvestedValue !== undefined) {
        setProductAtom((prev) => {
          return {
            ...prev,
            [product.marketTypeNormalized]: {
              ...(prev[product.marketTypeNormalized] || product),
              valueNormalized:
                data.totalInvestedValue ?? prev[product.marketTypeNormalized]?.valueNormalized ?? 0,
            },
          };
        });
      }

      setAllData(items || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, data, setAllData, setProductAtom]);

  if (isMobile) {
    return (
      <ProductTableMobile
        data={allData}
        isLoading={loading}
        fields={mobileFields}
        columns={mobileColumns}
        rows={mobileRows}
        secondaryInfo={mobileSecondaryInfo}
        quoteButtonInfoGetter={quoteButtonInfoGetter}
        recommendedPortfolioInfoGetter={recommendedPortfolioInfoGetter}
      />
    );
  }

  if (isTablet) {
    return (
      <ProductTableTablet
        data={allData}
        isLoading={loading}
        fields={tabletFields || mobileFields}
        columns={tabletColumns}
        rows={tabletRows}
        product={product}
        secondaryInfo={tabletSecondaryInfo}
        quoteButtonInfoGetter={quoteButtonInfoGetter}
        recommendedPortfolioInfoGetter={recommendedPortfolioInfoGetter}
      />
    );
  }

  if (loading) {
    return (
      <Box overflow={'hidden'}>
        <UI.Table isLoading={false} cols={columns} rows={5}>
          {renderDynamicTableHead(tableHeadItems)}
          <LoadingTable columnsItems={tableHeadItems} />
        </UI.Table>
      </Box>
    );
  }

  if (error) {
    return <HStack onTouchMove={refresher} />;
  }

  if (allData?.length === 0 && !loading) {
    return <NotFoundTable />;
  }

  const loadingFooter = () => renderDynamicLoadingFooter(loading, tableHeadItems);

  return (
    <Box overflow={'hidden'}>
      <UI.Table isLoading={loading} cols={columns} rows={5}>
        {renderDynamicTableHead(tableHeadItems)}

        <FlatList
          windowSize={20}
          maxToRenderPerBatch={20}
          updateCellsBatchingPeriod={5}
          data={allData}
          contentContainerStyle={{ maxHeight }}
          ListEmptyComponent={<LoadingTable columnsItems={tableHeadItems} />}
          showsVerticalScrollIndicator={false}
          onEndReachedThreshold={1.5}
          ItemSeparatorComponent={ItemSeparator}
          ListFooterComponent={loadingFooter}
          renderItem={({ item }) => (
            <ProductItem
              product={product}
              item={item}
              quoteButtonInfoGetter={quoteButtonInfoGetter}
            />
          )}
        />
      </UI.Table>
    </Box>
  );
};
